/**
 * Signup / campaign tracking: persist URL params and referrer in sessionStorage
 * so they survive navigation within the tab. On signup we merge sessionStorage + current URL params.
 */

const STORAGE_KEY = 'penning_signup_tracking'

/** URL param keys we capture (UTM, Google/Ads IDs, Penning visitor ID, etc.) */
export const TRACKING_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'gclid',
  'gbraid',
  'wbraid',
  '_pn_vid',
] as const

export type TrackingParamKey = (typeof TRACKING_PARAM_KEYS)[number]

/** Extract tracking params from a search string (e.g. location.search) */
export function getTrackingParamsFromSearch(
  search: string
): Record<string, string> {
  const params = new URLSearchParams(search)
  const result: Record<string, string> = {}
  for (const key of TRACKING_PARAM_KEYS) {
    const value = params.get(key)
    if (value != null && value !== '') result[key] = value
  }
  return result
}

/** Any UTM param (utm_*) for forward compatibility */
export function getAnyUtmFromSearch(search: string): Record<string, string> {
  const params = new URLSearchParams(search)
  const result: Record<string, string> = {}
  params.forEach((value, key) => {
    if (key.startsWith('utm_') && value !== '') result[key] = value
  })
  return result
}

export interface PersistedTracking {
  params: Record<string, string>
  landingPage: string | null
  referrer: string | null
}

function readPersisted(): PersistedTracking | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedTracking
    if (!parsed || typeof parsed.params !== 'object') return null
    return {
      params: parsed.params ?? {},
      landingPage:
        typeof parsed.landingPage === 'string' ? parsed.landingPage : null,
      referrer: typeof parsed.referrer === 'string' ? parsed.referrer : null,
    }
  } catch {
    return null
  }
}

function writePersisted(data: PersistedTracking): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // ignore
  }
}

/**
 * Persist current URL tracking params and, on first touch, landing URL + referrer.
 * Call on landing (e.g. / or /login) and when entering /sign-up so we don't lose params.
 */
export function persistTrackingParams(search: string, pathname: string): void {
  const fromSearch = {
    ...getTrackingParamsFromSearch(search),
    ...getAnyUtmFromSearch(search),
  }
  const hasParams = Object.keys(fromSearch).length > 0
  const currentFullUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${pathname}${search || ''}`
      : ''
  const currentReferrer =
    typeof document !== 'undefined' ? document.referrer || '' : ''

  const existing = readPersisted()

  // First touch: store landing page and referrer (even without UTM, for attribution)
  const landingPage = existing?.landingPage ?? (currentFullUrl || null)
  const referrer = existing?.referrer ?? (currentReferrer || null)

  const mergedParams = {
    ...(existing?.params ?? {}),
    ...fromSearch,
  }

  // Only persist if we have something to store
  if (Object.keys(mergedParams).length > 0 || landingPage || referrer) {
    writePersisted({
      params: mergedParams,
      landingPage,
      referrer,
    })
  }
}

/**
 * Read the Penning first-party visitor ID (_pn_vid) used to link anonymous
 * browsing sessions to signups for marketing attribution.
 * Priority: URL query param (?_pn_vid=...) → .penning.io cookie (_pn_vid).
 */
export function getPenningVisitorId(search: string): string | null {
  const fromUrl = new URLSearchParams(search).get('_pn_vid')
  if (fromUrl) return fromUrl

  if (typeof document !== 'undefined') {
    const match = document.cookie
      .split(';')
      .map(c => c.trim())
      .find(c => c.startsWith('_pn_vid='))
    if (match) return match.slice('_pn_vid='.length) || null
  }

  return null
}

/**
 * Get merged tracking params + landing_page + referrer for the signup API.
 * Prefer current URL search, then merge with persisted (so direct /sign-up?utm_... works).
 */
export function getTrackingPayload(
  currentSearch: string,
  currentPathname: string
): {
  params: Record<string, string>
  landingPage: string | null
  referrer: string | null
} {
  const fromUrl = {
    ...getTrackingParamsFromSearch(currentSearch),
    ...getAnyUtmFromSearch(currentSearch),
  }
  const existing = readPersisted()
  const mergedParams = {
    ...(existing?.params ?? {}),
    ...fromUrl,
  }
  const fullUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${currentPathname}${currentSearch || ''}`
      : ''
  return {
    params: mergedParams,
    landingPage: existing?.landingPage ?? (fullUrl || null),
    referrer:
      existing?.referrer ??
      (typeof document !== 'undefined' ? document.referrer || null : null),
  }
}
